import { FlowTrackerSteps, SELECT_INTERNAL_ACCOUNT_FAQ } from 'common/constants';
import {
  ATBeaconNotification,
  ATSelectionTile,
} from 'components/common/beacon/beaconRegistration';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useLazyGetEligibleManualAccountsProviderQuery,
  useLazyGetEligibleYodleeAccountsProviderQuery,
} from 'src/api/atv/atvApi';
import { PageRoutes } from 'src/common/constants';
import Accordion from 'src/components/common/Accordion/Accordion';
import { RootState } from 'src/state/store';
import { isSelectedAccountIneligible } from 'utils/accountUtils';
import { TransferFlow as TransferFlowEnum } from 'utils/constants';
import { setSelectedInternalAccount } from '../../transferSlice';
import { IInternalAccountState } from '../../transferSlice.types';
import '../selectInternalAccount/selectInternalAccount.scss';
import AccountList from './AccountList';

interface SelectInternalAccountProps {
  setIsStepValid: (isEnabled: boolean) => void;
  showValidation: boolean;
  setShowValidation: (isEnabled: boolean) => void;
}

const SelectInternalAccount: FC<SelectInternalAccountProps> = (props) => {
  const { setIsStepValid, showValidation, setShowValidation } = props;
  const transferFlow = useSelector((state: RootState) => state.transfer.transferFlow);
  const {
    triggerQuery: triggerManualQuery,
    data: manualData,
    error: manualError,
    isLoading: manualLoading,
  } = useLazyGetEligibleManualAccountsProviderQuery();

  const {
    triggerQuery: triggerYodleeQuery,
    data: yodleeData,
    error: yodleeError,
    isLoading: yodleeLoading,
  } = useLazyGetEligibleYodleeAccountsProviderQuery();

  useEffect(() => {
    (transferFlow === TransferFlowEnum.Manual ||
      transferFlow === TransferFlowEnum.AutomatedIncomplete
      ? triggerManualQuery
      : triggerYodleeQuery)();
  }, [transferFlow, triggerManualQuery, triggerYodleeQuery]);
  const navigate = useNavigate();
  const isLoading =
    transferFlow === TransferFlowEnum.Manual ||
    transferFlow === TransferFlowEnum.AutomatedIncomplete
      ? manualLoading
      : yodleeLoading;
  const error = transferFlow === TransferFlowEnum.Manual ? manualError : yodleeError;
  const internalAccounts =
    transferFlow === TransferFlowEnum.Manual ||
    transferFlow === TransferFlowEnum.AutomatedIncomplete
      ? manualData
      : yodleeData;
  const [eligibleAccounts, ineligibleAccounts] = [true, false].map((isEligible) =>
    internalAccounts?.filter((account) => account.isTransferEligible === isEligible),
  );

  const dispatch = useDispatch();
  if (
    isSelectedAccountIneligible(
      useSelector((state: RootState) => state.transfer.selectedInternalAccount),
      internalAccounts,
    )
  ) {
    dispatch(setSelectedInternalAccount(null));
  }
  const selectedInternalAccount = useSelector(
    (state: RootState) => state.transfer.selectedInternalAccount,
  );
  const selectedAccountId = selectedInternalAccount
    ? selectedInternalAccount.selectedId
    : null;
  const handleSelectAccountType = (
    selectedAccountType: string,
    selectedId: number,
    noAccounts: boolean,
  ) => {
    const account = internalAccounts?.find((acc: any) => acc?.agreementId === selectedId);
    let accountHolderName = '';
    let accountNumber = '';
    if (account) {
      accountNumber = account?.agreementNumber ?? '';
      accountHolderName = account.ownerNames?.join(', ') ?? '';
    }

    const internalAccountObj: IInternalAccountState = {
      selectedAccountType: selectedAccountType,
      selectedId: selectedId,
      noAccounts,
      accountHolderName,
      accountNumber,
    };
    dispatch(setSelectedInternalAccount(internalAccountObj));
    setIsStepValid(true);
  };

  useEffect(() => {
    if (selectedAccountId) {
      setIsStepValid(true);
      setShowValidation(false);
    } else {
      setIsStepValid(false);
    }
  }, [selectedAccountId, setIsStepValid, setShowValidation]);

  useEffect(() => {
    if (error) {
      navigate(PageRoutes.ERROR);
    }
  }, [error, navigate]);

  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid Internal-account">
          <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            {isLoading && <div>Loading...</div>}
            {!!error && <div>Error loading Internal accounts</div>}
            {showValidation && (
              <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col b-padding-block-end--micro--jumbo">
                <ATBeaconNotification
                  type="inline"
                  variant="warning"
                  dismissible
                  onBeaconEventDismiss={() => setShowValidation(false)}
                >
                  Please choose an account to move forward
                </ATBeaconNotification>
              </div>
            )}
            {/* Eligible Accounts */}
            {(eligibleAccounts?.length ?? 0) > 0 && (
              <AccountList
                className="eligible-Accounts"
                accounts={eligibleAccounts ?? []}
                title="You have one or more eligible accounts"
                selectedAccountId={selectedAccountId ?? 0}
                handleSelectAccountType={handleSelectAccountType}
                selectedInternalAccount={selectedInternalAccount}
              />
            )}

            {/* Ineligible Accounts */}
            {(ineligibleAccounts?.length ?? 0) > 0 && (
              <AccountList
                className="ineligible-Accounts"
                accounts={ineligibleAccounts ?? []}
                title="You have one or more ineligible accounts"
                selectedAccountId={selectedAccountId ?? 0}
                handleSelectAccountType={handleSelectAccountType}
                selectedInternalAccount={selectedInternalAccount}
              />
            )}
          </div>
        </div>
        {internalAccounts && (
          <>
            <hr className="separator-line" />
            <div className="b-grid">
              <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:6-col b-lg:6-col">
                <ATSelectionTile>
                  <label slot="label" htmlFor="selection-no-account">
                    Open a new account for this transfer
                  </label>
                  <input
                    id="selection-no-account"
                    type="radio"
                    name="selection"
                    checked={selectedInternalAccount?.noAccounts}
                    onChange={() => handleSelectAccountType('noAccountType', -1, true)}
                  />
                </ATSelectionTile>
              </div>
            </div>
            <div className="b-typography-sans-serif--body--100 b-font-weight-light newAccounts-info">
              By choosing this option, you’ll be able to view or select the account type
              to open, verify your contact information and answer a few regulatory
              questions.
            </div>
          </>
        )}
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <div className="flow-tracker-container">
          <FlowTracker currentFlowStep={3} items={FlowTrackerSteps} />
        </div>
        <div className="faq-container">
          <Accordion items={SELECT_INTERNAL_ACCOUNT_FAQ} />
        </div>
      </div>
    </div>
  );
};

export default SelectInternalAccount;
